const getAmount = () =>
    window.Alpine.reactive({
        _value: 0,
        _text: 0,
        set value(v) {
            this._value = v;
            this._text = window.Alpine.store("currency").format(v, {
                digit: 0,
            });
        },
        get value() {
            return this._value;
        },
        get text() {
            return this._text;
        },
    });

export const usePaymentRequest = () => {
    const methods = {
        _data: [],
        get value() {
            return this._data;
        },
        set value(m) {
            this._data = m;
        },
    };
    const minAmount = getAmount();
    const maxAmount = getAmount();
    const amount = window.Alpine.reactive({
        _value: 0,
        _isValid: true,
        filter(v) {
            if (typeof v == "string") {
                return v ? parseFloat(v.replace(/[^\d.-]/g, "")) || 0 : 0;
            } else if (typeof v == "number") {
                return v;
            } else {
                return 0;
            }
        },
        validate(v) {
            const filteredValue = this.filter(v);
            let validValue = filteredValue;
            if (filteredValue <= minAmount.value) {
                validValue = minAmount.value;
            } else if (filteredValue >= maxAmount.value) {
                validValue = maxAmount.value;
            }
            return {
                validValue,
                filteredValue,
                isValid: validValue == filteredValue,
            };
        },
        get value() {
            return this._value;
        },
        set value(v) {
            const { filteredValue, isValid } = this.validate(v);
            this._value = filteredValue;
            this._isValid = isValid;
        },
        get text() {
            return window.Alpine.store("currency").format(this._value) ?? "";
        },
        get isValid() {
            return this._isValid;
        },
    });
    const options = {
        _data: {},
        reset() {
            this._data = {};
        },
        push(key, value) {
            if (!key.includes(".")) {
                this._data[key] = value;
                return;
            }
            const segments = key.split(".");
            let current = this._data;
            for (let i = 0; i < segments.length - 1; i++) {
                const segment = segments[i];
                if (!current[segment]) {
                    current[segment] = {};
                }
                current = current[segment];
            }
            current[segments[segments.length - 1]] = value;
        },
        remove(key) {
            if (!key.includes(".")) {
                delete this._data[key];
                return;
            }

            const segments = key.split(".");
            let current = this._data;

            for (let i = 0; i < segments.length - 1; i++) {
                const segment = segments[i];
                if (!current[segment]) {
                    return;
                }
                current = current[segment];
            }

            delete current[segments[segments.length - 1]];
        },
        get value() {
            return this._data;
        },
    };
    const details = {
        _data: {},
        reset() {
            this._data = {};
        },
        push(key, value) {
            this._data[key] = value;
        },
        get value() {
            return this._data;
        },
    };

    const selected = window.Alpine.reactive({
        _selected: null,
        get value() {
            return this._selected;
        },
        set value(s) {
            const isInitialized = this._selected != null;
            if (!isInitialized) {
                // no selected method yet, set the amount to the min limit of the method received.
                amount.value = s.min_limit;
            }
            this._selected = s;
            // options.reset();
            minAmount.value = s.min_limit;
            maxAmount.value = s.max_limit;

            if (isInitialized) {
                const { validValue } = amount.validate(amount.value);
                // must run only if value is already set.
                amount.value = validValue;
            }
        },
        detect() {
            if (methods.value.length > 0) {
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);
                const methodValue = urlParams.get("method");
                if (methodValue) {
                    const intValue = parseInt(methodValue, 10);
                    if (String(intValue) === methodValue) {
                        const index = methods.value.findIndex(
                            (m) => m.id == intValue
                        );
                        if (index >= 0) {
                            this.value = methods.value[index];
                            return this.value;
                        }
                    }
                }
                this.value = methods.value[0];
                return this.value;
            } else {
                console.log("METHODS:", 0);
                return null;
            }
        },
    });

    /**
     * Generates a random amount based on a given value with specific constraints
     * @param {number} amount - The base amount to modify
     * @param {number} [minimum=0] - The minimum allowed value for both input and output
     * @param {number} [maximum=0] - The maximum allowed value for both input and output
     * @returns {number} - Modified amount that meets all constraints
     * @throws {Error} - If parameters are invalid or if constraints cannot be satisfied
     */
    function randomize(amount, minimum = 0, maximum = 0) {
        const validate = (amount, minimum, maximum) =>
            amount >= minimum && // Not less than minimum
            ((maximum > 0 && amount < maximum) || maximum <= 0) && // Not more than maximum
            (amount % 10 === 5 || amount % 10 === 0) && // Ends in 0 or 5
            !(amount % 100 === 0); // Doesn't end in double zero

        // Input validation
        if (typeof amount !== "number" || isNaN(amount)) {
            throw new Error("Amount must be a valid number");
        }
        if (amount < minimum) {
            throw new Error("Base amount cannot be less than minimum");
        }

        if (validate(amount, minimum, maximum)) {
            return amount;
        }

        // Determine percentage range based on amount
        const minPercent = amount < 100 ? 10 : 1;
        const maxPercent = amount < 100 ? 30 : 3;

        let attempts = 0;
        const maxAttempts = 100;
        let newAmount;
        let randomPercent;

        while (attempts++ < maxAttempts) {
            // Default behavior
            randomPercent =
                (Math.random() * (maxPercent - minPercent) + minPercent) *
                (Math.random() < 0.5 ? -1 : 1);

            if (maximum > 0) {
                // cap max percent if amount is too close to the max value.
                const maxAllowedPositivePercent =
                    ((maximum - amount) / amount) * 100;

                if (maxAllowedPositivePercent < minPercent) {
                    // If maximum is too close, only geenrate negatives percentages
                    randomPercent = -(
                        Math.random() * (maxPercent - minPercent) +
                        minPercent
                    );
                } else if (maxAllowedPositivePercent < maxPercent) {
                    // If mamximum allows only limited positives percentages
                    const usePositive = Math.random() < 0.5;
                    if (usePositive) {
                        randomPercent =
                            Math.random() *
                                (maxAllowedPositivePercent - minPercent) +
                            minPercent;
                    } else {
                        randomPercent = -(
                            Math.random() * (maxPercent - minPercent) +
                            minPercent
                        );
                    }
                }
            }

            // Calculate new amount with random variation
            newAmount = amount * (1 + randomPercent / 100);

            // Round to nearest 5
            newAmount = Math.round(newAmount / 5) * 5;

            // Validate all constraints
            const isNewAmountValid =
                newAmount !== amount && validate(newAmount, minimum, maximum);

            if (isNewAmountValid) {
                return newAmount;
            }
        }

        throw new Error(
            `Could not generate valid random amount within constraints after ${maxAttempts} attempts.`
        );
    }

    return {
        methods,
        selected,
        amount,
        details,
        options,
        minAmount,
        maxAmount,
        randomize,
    };
};
